import { classes } from "polytype";
import { ContributorExtensionAssetAccessTokenProvider } from "./ContributorExtensionAssetAccessTokenProvider";
import type {
  ContributorExtensionData,
  ContributorExtensionListItemData,
  ContributorExtensionListQueryData,
  ContributorExtensionUpdateRequestData,
  MarketplaceExtensionDeprecation,
  MarketplaceExternalComponent,
  MarketplaceFrontendFragment,
  MarketplaceSupportMeta,
  MarketplaceWebhookUrls,
} from "./types.js";
import type { ValidationError } from "../../base";
import {
  DataModel,
  ListDataModel,
  ListQueryModel,
  ReferenceModel,
  Money,
} from "../../base";
import type { AsyncResourceVariant } from "../../react";
import { provideReact } from "../../react";
import { config } from "../../config/config.js";
import assertObjectFound from "../../base/assertObjectFound.js";
import { Contributor } from "../Contributor";
import type { MarketplaceContext } from "../ExtensionInstance";
import { type DomFile, File } from "../../file";
import { ExtensionAsset } from "../Extension/ExtensionAsset.js";
import { ContributorExtensionLogoAccessTokenProvider } from "./ContributorExtensionLogoAccessTokenProvider.js";

export class ContributorExtension extends ReferenceModel {
  public readonly contributorId: string;
  public readonly logoAccessTokenProvider: ContributorExtensionLogoAccessTokenProvider;
  public readonly assetAccessTokenProvider: ContributorExtensionAssetAccessTokenProvider;

  public constructor(contributorId: string, extensionId: string) {
    super(extensionId);
    this.contributorId = contributorId;
    this.logoAccessTokenProvider =
      new ContributorExtensionLogoAccessTokenProvider(this);
    this.assetAccessTokenProvider =
      new ContributorExtensionAssetAccessTokenProvider(this);
  }

  public static ofId(contributorId: string, extensionId: string) {
    return new ContributorExtension(contributorId, extensionId);
  }

  public static find = provideReact(
    async (contributorId: string, extensionId: string) => {
      const data = await config.behaviors.contributorExtension.find(
        contributorId,
        extensionId,
      );

      if (data) {
        return new ContributorExtensionDetailed(data);
      }
    },
  );

  public static get = provideReact(
    async (contributorId: string, extensionId) => {
      const extension = await this.find(contributorId, extensionId);
      assertObjectFound(extension, "extension", extensionId);
      return extension;
    },
  );

  public static async create(data: { contributorId: string; name: string }) {
    const { contributorId, name } = data;

    const { id } = await config.behaviors.contributorExtension.create(
      contributorId,
      name,
    );
    return new ContributorExtension(contributorId, id);
  }

  public async update(data: ContributorExtensionUpdateRequestData) {
    return await config.behaviors.contributorExtension.update(
      this.contributorId,
      this.id,
      data,
    );
  }

  public async delete() {
    return await config.behaviors.contributorExtension.delete(
      this.contributorId,
      this.id,
    );
  }

  public async publish() {
    return await config.behaviors.contributorExtension.publish(
      this.contributorId,
      this.id,
    );
  }

  public async updateContext(context: MarketplaceContext) {
    return await config.behaviors.contributorExtension.updateContext(
      context,
      this.contributorId,
      this.id,
    );
  }

  public findDetailed = provideReact(
    () => ContributorExtension.find(this.contributorId, this.id),
    [this.id],
  ) as AsyncResourceVariant<
    () => Promise<ContributorExtensionDetailed | undefined>
  >;

  public getDetailed = provideReact(
    () => ContributorExtension.get(this.contributorId, this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<ContributorExtensionDetailed>>;

  public async uploadLogo(file: DomFile) {
    await File.upload(file, this.logoAccessTokenProvider);
  }

  public async uploadAsset(file: DomFile, assetType: "image" | "video") {
    await File.upload(file, this.assetAccessTokenProvider, assetType);
  }

  public async deleteExtensionAsset(assetRefId: string) {
    return await config.behaviors.contributorExtension.deleteExtensionAsset(
      this.contributorId,
      this.id,
      assetRefId,
    );
  }

  public getLogoUploadRules = provideReact(async () => {
    return File.getUploadRules("avatar");
  });

  public getMediaImageUploadRules = provideReact(async () => {
    return File.getUploadRules("extensionAssetImage");
  });

  public getMediaVideoUploadRules = provideReact(async () => {
    return File.getUploadRules("extensionAssetVideo");
  });

  public getPossibleScopes = provideReact(async () => {
    return await config.behaviors.contributorExtension.getPossibleScopes();
  });

  public static query(
    contributor: Contributor,
    query: ContributorExtensionListQueryData = {},
  ) {
    return new ContributorExtensionListQuery(contributor, query);
  }
}

class ContributorExtensionCommon extends classes(
  DataModel<ContributorExtensionData | ContributorExtensionListItemData>,
  ContributorExtension,
) {
  public readonly contributorId: string;
  public readonly contributor: Contributor;
  public readonly name: string;
  public readonly description?: string;
  public readonly detailedDescriptionMarkdown?: string;
  public readonly subTitle?: string;
  public readonly context?: MarketplaceContext;
  public readonly scopes?: string[];
  public readonly logo?: File;
  public readonly externalUrl?: string;
  public readonly assets: ExtensionAsset[];
  public readonly amountOfInstances: number;
  public readonly pricing?: Money;
  public readonly blocked?: boolean;
  public readonly deprecation?: MarketplaceExtensionDeprecation;
  public readonly externalFrontends?: MarketplaceExternalComponent[];
  public readonly frontendFragments?: Record<
    string,
    MarketplaceFrontendFragment
  >;
  public readonly requestedChanges?: {
    context?: MarketplaceContext;
    scopes?: string[];
    webhookUrls?: Record<string, never> | MarketplaceWebhookUrls;
  };
  public readonly state?: "enabled" | "blocked" | "disabled";
  public readonly tags?: string[];
  public readonly support?: MarketplaceSupportMeta;
  public readonly verificationRequested: boolean;
  public readonly verified: boolean;
  public readonly webhookUrls?: MarketplaceWebhookUrls;

  public readonly published?: boolean;
  public constructor(
    data: ContributorExtensionData | ContributorExtensionListItemData,
  ) {
    const { id, contributorId } = data;
    super([data], [contributorId, id]);
    this.contributorId = contributorId;
    this.contributor = Contributor.ofId(contributorId);
    this.name = data.name;
    this.description = data.description;
    // todo: Sprache prüfen und entsprechend laden
    this.detailedDescriptionMarkdown = data.detailedDescriptions?.de.markdown;
    // todo: Sprache prüfen und entsprechend laden
    this.subTitle = data.subTitle?.de;
    this.context = data.context;
    this.scopes = data.scopes;
    this.logo = data.logoRefId ? File.ofId(data.logoRefId) : undefined;
    this.externalUrl = data.frontendComponents?.[0]?.url;
    this.assets = data.assets
      .sort((a, b) => b.index - a.index)
      .map((a) => new ExtensionAsset(a));
    this.amountOfInstances = this.data.statistics.amountOfInstances ?? 0;
    if (data.pricing?.netPrice) {
      this.pricing = Money({ amount: data.pricing.netPrice, currency: "EUR" });
    }

    this.blocked = data.blocked;
    this.deprecation = data.deprecation;
    this.externalFrontends = data.externalFrontends;
    this.frontendFragments = data.frontendFragments;
    this.requestedChanges = data.requestedChanges;
    this.state = data.state;
    this.tags = data.tags;
    this.support = data.support;
    this.verificationRequested = data.verificationRequested;
    this.verified = data.verified;
    this.webhookUrls = data.webhookUrls;

    this.published = data.published;
  }

  public async updateDetails(
    title: string,
    subTitleDE: string,
    description: string,
    detailedDescriptionDE?: string,
  ) {
    if (detailedDescriptionDE) {
      return await this.update({
        name: title,
        subTitle: {
          de: subTitleDE,
        },
        description: description,
        detailedDescriptions: {
          de: {
            markdown: detailedDescriptionDE,
          },
        },
      });
    }
    return await this.update({
      name: title,
      subTitle: {
        de: subTitleDE,
      },
      description: description,
    });
  }

  public async updateSupport(email: string, phone?: string) {
    const response = await this.update({
      support: {
        email: email,
        phone: phone,
      },
    });

    if (response) {
      return response.map((e) => ({
        ...e,
        path: e.path.includes("mail") ? "email" : e.path,
      }));
    }
  }

  public async updateExternalFrontend(url: string) {
    return await this.update({
      externalFrontends: [
        {
          name: "external_frontend",
          url: url,
        },
      ],
    });
  }
  public async addFrontendFragment(name: string, url: string, anchor: string) {
    const newFragment: MarketplaceFrontendFragment = {
      additionalProperties: {
        title: JSON.stringify({ de: name }),
        anchor,
      },
      url,
    };

    const updatedFragments = {
      ...(this.frontendFragments ?? {}),
      [anchor]: newFragment,
    };

    const response = await this.update({
      frontendFragments: updatedFragments,
    });

    if (response) {
      return response.map((e) => ({
        ...e,
        path: e.path == "frontendFragments" ? "url" : e.path,
      }));
    }
  }

  public async editFrontendFragment(
    name: string,
    url: string,
    newAnchor: string,
    originalAnchor: string,
  ) {
    const newFragment: MarketplaceFrontendFragment = {
      additionalProperties: {
        title: JSON.stringify({ de: name }),
        anchor: newAnchor,
      },
      url,
    };

    const fragments = { ...(this.frontendFragments ?? {}) };

    if (originalAnchor && originalAnchor !== newAnchor) {
      delete fragments[originalAnchor];
    }

    fragments[newAnchor] = newFragment;

    const response = await this.update({
      frontendFragments: fragments,
    });

    if (response) {
      return response.map((e) => ({
        ...e,
        path: e.path === "frontendFragments" ? "url" : e.path,
      }));
    }
  }

  public async deleteExternalFrontend() {
    return await this.update({
      externalFrontends: null as unknown as undefined,
    });
  }

  public async deleteFrontendFragment(key: string) {
    const fragments = { ...this.frontendFragments };
    delete fragments[key];

    return await this.update({
      frontendFragments: fragments,
    });
  }

  public async updateWebhooksSingle(webhookUrl: string) {
    const response = await this.update({
      webhookUrls: {
        extensionAddedToContext: {
          url: webhookUrl,
        },
        extensionInstanceUpdated: {
          url: webhookUrl,
        },
        extensionInstanceSecretRotated: {
          url: webhookUrl,
        },
        extensionInstanceRemovedFromContext: {
          url: webhookUrl,
        },
      },
    });

    if (response) {
      return response.map((e) => ({
        ...e,
        path: e.path === "webhookUrls" ? "oneHookUrl" : e.path,
      }));
    }
  }

  public async updateWebhooksMultiple(
    extensionAddedToContext: string,
    extensionInstanceUpdated: string,
    extensionInstanceSecretRotated: string,
    extensionInstanceRemovedFromContext: string,
  ) {
    const response = await this.update({
      webhookUrls: {
        extensionAddedToContext: {
          url: extensionAddedToContext,
        },
        extensionInstanceUpdated: {
          url: extensionInstanceUpdated,
        },
        extensionInstanceSecretRotated: {
          url: extensionInstanceSecretRotated,
        },
        extensionInstanceRemovedFromContext: {
          url: extensionInstanceRemovedFromContext,
        },
      },
    });

    const customValidationError: ValidationError[] = [];

    if (response) {
      response.map((e) => {
        if (e.context?.["SecretRotated"]) {
          customValidationError.push({
            path: "extensionInstanceSecretRotated",
            type: "invalid",
          });
        }
        if (e.context?.["InstanceRemovedFromContext"]) {
          customValidationError.push({
            path: "extensionInstanceRemovedFromContext",
            type: "invalid",
          });
        }
        if (e.context?.["ExtensionAddedToContext"]) {
          customValidationError.push({
            path: "extensionAddedToContext",
            type: "invalid",
          });
        }
        if (e.context?.["InstanceUpdated"]) {
          customValidationError.push({
            path: "extensionInstanceUpdated",
            type: "invalid",
          });
        }
      });
    }
    if (customValidationError.length != 0) return customValidationError;
  }

  public async deleteWebhooks() {
    return await this.update({
      webhookUrls: null as unknown as MarketplaceWebhookUrls,
    });
  }

  public async updateContext(context: MarketplaceContext) {
    return await config.behaviors.contributorExtension.updateContext(
      context,
      this.contributorId,
      this.id,
    );
  }

  public async updateScopes(scopes: string[]) {
    return await this.update({ scopes: scopes });
  }

  public async updatePricing(price: number) {
    return await config.behaviors.contributorExtension.updatePricing(
      this.contributorId,
      this.id,
      price,
    );
  }

  public async publishExtension() {
    return await this.publish();
  }
}

export class ContributorExtensionDetailed extends classes(
  ContributorExtensionCommon,
  DataModel<ContributorExtensionData>,
) {
  public constructor(data: ContributorExtensionData) {
    super([data], [data]);
  }
}

export class ContributorExtensionListItem extends classes(
  ContributorExtensionCommon,
  DataModel<ContributorExtensionListItemData>,
) {
  public constructor(data: ContributorExtensionListItemData) {
    super([data], [data]);
  }
}

export class ContributorExtensionListQuery extends ListQueryModel<ContributorExtensionListQueryData> {
  public readonly contributor: Contributor;
  public constructor(
    contributor: Contributor,
    query: ContributorExtensionListQueryData = {},
  ) {
    super(query, {
      dependencies: [contributor.id],
    });
    this.contributor = contributor;
  }

  public refine(query: ContributorExtensionListQueryData) {
    return new ContributorExtensionListQuery(this.contributor, {
      ...this.query,
      ...query,
    });
  }

  public execute = provideReact(async () => {
    const { items, totalCount } =
      await config.behaviors.contributorExtension.list(
        this.contributor.id,
        this.query,
      );

    return new ContributorExtensionList(
      this.contributor,
      this.query,
      items.map((d) => new ContributorExtensionListItem(d)),
      totalCount,
    );
  }, [this.queryId, "contributorExtension"]);

  public getTotalCount = provideReact(async () => {
    const { totalCount } = await this.refine({ limit: 1 }).execute();
    return totalCount;
  }, [this.queryId, "contributorExtension"]);
}

export class ContributorExtensionList extends classes(
  ContributorExtensionListQuery,
  ListDataModel<ContributorExtensionListItem>,
) {
  public constructor(
    contributor: Contributor,
    query: ContributorExtensionListQueryData,
    extensions: ContributorExtensionListItem[],
    totalCount: number,
  ) {
    super([contributor, query], [extensions, totalCount]);
  }
}
